/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import React, {
  ChangeEvent,
  forwardRef,
  MouseEventHandler,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState
} from 'react';
import {makeStyles} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {entityId} from '@ion/core/src/services/ConnectUtils';
import {
  Account,
  Link,
  MessageEndpoint as MessageEndpointType,
  MessageEndpointConfiguration,
  MessageEndpointTypes
} from '@ion/core/src/types/entities';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {TertiaryButton} from '@ion/dls/src/components/buttons/TertiaryButton';
import {FormItem} from '@ion/dls/src/components/forms/FormItem';
import {FormSection} from '@ion/dls/src/components/forms/FormSection';
import {IonSpinner} from '@ion/dls/src/components/loading/IonSpinner';
import Dropdown from '@ion/dls/src/components/menus/Dropdown';
import {FormState, useFormState} from '@ion/dls/src/hooks/useFormState';
import Render from '@ion/dls/src/render/Render';
import useAccountMessageEndpoint from '@ion/query/src/hooks/useAccountMessageEndpoint';
import GCPPubSubMessageEndpoint from './GCPPubSubMessageEndpoint';
import KafkaMessageEndpoint from './KafkaMessageEndpoint';
import KafkaProducerProperties from './KafkaProducerProperties';
import {
  getInitialStreamFormState,
  getIsGCPPubSubType,
  getIsKafkaPlainOrScramType,
  getIsKafkaType,
  getIsStreamType,
  MessageEndpointFormFields,
  validateMessageEndpoint
} from './MessageEndpointUtils';
import StreamMessageEndpoint from './StreamMessageEndpoint';
import useShowMessageEndpoint from './useShowMessageEndpoint';

export interface MessageEndpointProps {
  account: Account;
  subscription: Link;
  isView?: boolean;
  isEdit?: boolean;
  enabled?: boolean;
  show?: boolean;
  isLoggedinUserAccount?: boolean;
};

// API for the MessageEndpoint useImperativeHandler ref
export type MessageEndpointAPI = {
  validate: () => boolean;
  getMessageEndpointState: () => FormState | null;
}

export interface LegendPropTypes {
  onClear: MouseEventHandler<HTMLButtonElement>;
};

/**
 * Component for displaying the Message Endpoint Add / Delete CTA
 */
const Legend = ({onClear}: LegendPropTypes) => {
  return (
    <TertiaryButton onClick={onClear}>
      {translate('common:clear')}
    </TertiaryButton>
  );
};

const useStyles = makeStyles(() => ({
  container : {
    margin : '0px'
  },
  typoStyle : {
    fontSize : '14px !important',
    width    : '100%'
  },
  subHeader : {
    display : 'flex',
    width   : '100%',
    padding : '16px',
    height  : '0em'
  },
  iconStyle : {
    margin   : '-32px',
    position : 'relative',
    top      : '55px',
    left     : '25px'
  },
  loader : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  }
}));

/**
 * Form section component for maintaining (add / update / view / delete) an Account's Message Endpoint.
 *
 * ```typescript
 *  import MessageEndpoint from '@ion/dls/src/editors/accounts';
 *
 *  <MessageEndpoint ref={messageEndpointRef}
 *    account={group}
 *    show={true}
 *    isView={isViewMode}
 *    isEdit={isEditMode}
 *    enabled={isCtcAdmin} />
 * ```
 */
const MessageEndpointImpl = forwardRef(({
  account,
  show = false,
  enabled = false,
  isView = false,
  isEdit = false,
  isLoggedinUserAccount = false
}: MessageEndpointProps, ref) => {
  const classes                       = useStyles();
  const isAdd                         = isObjectEmpty(account);
  const [messageType, setMessageType] = useState(MessageEndpointTypes.AWS_KINESIS);
  const [isStream, setIsStream]       = useState(false);
  const [isKafka, setIsKafka]         = useState(false);
  const [isGCPPubSub, setIsGCPPubSub] = useState(false);
  const [original, setOriginal]       = useState<MessageEndpointType>();
  const kafkaPlainOrScram             = getIsKafkaPlainOrScramType(messageType);

  const {
    messageEndpoint,
    isLoading
  } = useAccountMessageEndpoint({
    accountId : String(account?.id),
    enabled   : isView || isEdit
  });

  useEffect(function(): void {
    if (messageEndpoint?.configuration) {
      setOriginal(messageEndpoint);
      setMessageType(messageEndpoint.configuration.type);
    }
  }, [messageEndpoint]);

  useEffect(function(): void {
    if (getIsStreamType(messageType)) {
      setIsStream(true);
      setIsKafka(false);
      setIsGCPPubSub(false);
    } else if (getIsKafkaType(messageType)) {
      setIsStream(false);
      setIsKafka(true);
      setIsGCPPubSub(false);
    } else if (getIsGCPPubSubType(messageType)) {
      setIsGCPPubSub(true);
      setIsStream(false);
      setIsKafka(false);
    }
    setFormState({
      [MessageEndpointFormFields.TYPE] : {
        name     : MessageEndpointFormFields.TYPE,
        value    : messageType,
        required : true
      }
    });
  }, [messageType]);

  const [formState, setFormState] = useFormState({
    [MessageEndpointFormFields.TYPE] : {
      name     : MessageEndpointFormFields.TYPE,
      required : true,
      value    : messageType
    }
  });

  useImperativeHandle(ref, () => ({
    getMessageEndpointState : function(): MessageEndpointType | null {
      const result = show ? {
        ...original,
        configuration : formState as MessageEndpointConfiguration
      } : null;
      return result;
    },
    validate : function() {
      if (enabled) {
        const errors = validateMessageEndpoint(formState) || {};
        if (!isObjectEmpty(errors)) {
          setFormState(errors);
          const firstErrorField = Object.keys(errors).find(key => errors[key]?.error);
          if (firstErrorField) {
            document.querySelector<HTMLElement>(`[name="${firstErrorField}"]`)
              ?.scrollIntoView({behavior: 'smooth', block: 'center'});
          }
          return false;
        }
      }
      return true;
    }
  }), [formState, show, original, enabled]);

  const onClear = useCallback(function() {
    setMessageType(MessageEndpointTypes.AWS_KINESIS);
    setFormState(getInitialStreamFormState(messageType, messageEndpoint?.configuration));
  }, [messageType]);

  const onTypeChange = useCallback(function(_target, child) {
    const newType = MessageEndpointTypes[child?.value];
    setMessageType(newType);
  }, [messageType]);

  const onFieldChange = useCallback(function(e: ChangeEvent<HTMLInputElement>) {
    e.persist();
    const {name, value, required = false} = e.target;
    setFormState({
      [name] : {
        value,
        name,
        required
      }
    });
  }, []);

  if (isLoading) {
    return (
      <FormItem fullWidth>
        <div className={classes.loader}>
          <IonSpinner />
        </div>
      </FormItem>
    );
  };

  return (
    <div className={classes.container}>
      <FormSection bottomBorder={true}
        title={translate('admin-groups:dialog.messageEndpoint.title')}
        legend={(!isView && !isEdit && !isLoggedinUserAccount) && <Legend onClear={onClear}/>}>
        <FormItem>
          <Dropdown required
            options={translate('admin-groups:dialog.messageEndpoint.fields.type.optionsWithKafka', {returnObjects: true})}
            fieldLabel={translate('admin-groups:dialog.messageEndpoint.fields.type.label')}
            errorText={translate('admin-groups:dialog.messageEndpoint.fields.type.errorMessage')}
            hasError={formState[MessageEndpointFormFields.TYPE]?.error}
            name={MessageEndpointFormFields.TYPE}
            value={messageType}
            onChange={onTypeChange}
            disabled={isView || isLoggedinUserAccount} />
        </FormItem>
        {isStream && (
          <StreamMessageEndpoint isView={isView}
            isAdd={isAdd}
            isEdit={isEdit}
            isLoggedinUserAccount={isLoggedinUserAccount}
            formState={formState}
            setFormState={setFormState}
            type={messageType}
            messageEndpoint={messageEndpoint}
            onFieldChange={onFieldChange} />
        )}
        {isKafka && (
          <KafkaMessageEndpoint isView={isView}
            isAdd={isAdd}
            isLoggedinUserAccount={isLoggedinUserAccount}
            formState={formState}
            setFormState={setFormState}
            messageEndpoint={messageEndpoint}
            type={messageType}
            onFieldChange={onFieldChange} />
        )}
        {isGCPPubSub && (
          <GCPPubSubMessageEndpoint isView={isView}
            isAdd={isAdd}
            isEdit={isEdit}
            isLoggedinUserAccount={isLoggedinUserAccount}
            formState={formState}
            setFormState={setFormState}
            messageEndpoint={messageEndpoint}
            type={messageType}
            onFieldChange={onFieldChange} />
        )}
      </FormSection>
      <Render.If condition={kafkaPlainOrScram}>
        <KafkaProducerProperties formState={formState}
          onFieldChange={onFieldChange}
          isView={isView}
          isLoggedinUserAccount={isLoggedinUserAccount} />
      </Render.If>
    </div>);
});

/**
 * Wrapper for determining if we should even show the Message Endpoint section.
 */
export const MessageEndpoint = forwardRef((props: MessageEndpointProps, ref) => {
  const canHaveMessageEndpoint = useShowMessageEndpoint(
    props.enabled,
    entityId(props.subscription)
  );
  if (canHaveMessageEndpoint) {
    return <MessageEndpointImpl {...props} show={canHaveMessageEndpoint} ref={ref} />;
  }
});

export default MessageEndpoint;
